import {
  useState,
  useMemo,
  ReactNode,
  useEffect,
  Dispatch,
  SetStateAction,
  createContext,
  createRef,
} from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import isEmpty from 'lodash/isEmpty';
import { useContextWithValidation } from '@adsk/alloy-react-context';
import { translate } from '../helpers/i18n';
import { useGetUsersMe } from '../hooks/queries';
import { useGetRfiTypes } from '../hooks/queries/rfiTypesQueries';
import useRfiNotifications from '../hooks/useRfiNotifications';
import { StatusOption, useStatusOptions } from '../hooks/useStatusOptions';
import useAnalytics from '../hooks/useAnalytics';
import { createIssue } from '../helpers/analytics-helpers';
import {
  getMandatoryFields,
  rfiCustomIdentifierAllowedCharactersValidator,
} from '../helpers/validation-helpers';
import { ISSUE_ATTRIBUTE_NAMES } from '../constants/issue-attribute-names';
import formatFormValuesForSubmission from './helpers/formatFormValuesForSubmission';
import { createSDK, RfiPriority, RfiStatus, RfiType, WorkflowType } from '../sdk';
import DataNormalize from '../helpers/dataNormalize';
import { getRfiTypeFieldsInUse, getSelectedTransition } from '../helpers';

type RfiFormContextProps = {
  setInvalidIdentifiers: any;
  setPrevSelectedTransition: any;
  setSelectedTransition: Dispatch<SetStateAction<StatusOption | undefined>>;
  createRfi: any;
  defaultValues: any;
  invalidIdentifiers: any;
  isRfiCustomIdentifierVisible: any;
  isSubmitting: any;
  mandatoryFields: any;
  prevSelectedTransition: any;
  projectId: any;
  ready: boolean;
  refs: any;
  selectedTransition: StatusOption | undefined;
  showNoWorkflowConfiguration: any;
  validationSchema: any;
};

const serializeRfi = DataNormalize.prototype.serializeRfi.bind(DataNormalize.prototype);

const RfiFormContext = createContext<RfiFormContextProps>({} as RfiFormContextProps);

const useRfiForm = () => {
  const rfiFormCtx = useContextWithValidation(RfiFormContext, {
    hook: 'useRfiForm',
    provider: 'RfiFormProvider',
  });

  return rfiFormCtx;
};

const useRfiFormLogic = ({ origin, featureFlag }: any) => {
  const usersMe = useGetUsersMe();
  const projectId = useCurrentProjectId();
  const { data: rfiTypes } = useGetRfiTypes();

  // Notifications
  const { showSuccessNotification, showErrorNotification } = featureFlag.notifications
    ? useRfiNotifications()
    : ({} as any);

  // Analytics
  const { trackEvent } = featureFlag.analytics ? useAnalytics() : ({} as any);
  const trackCreateRfi = (rfi: any) => {
    const rfiType = rfiTypes?.find(({ id }) => id === rfi.rfiTypeId);
    const isDefaultRfiType = rfiType?.isDefault;
    const rfiTypesFieldsInUse = getRfiTypeFieldsInUse((rfiType || []) as RfiType);
    const trackingEventObj = createIssue({ ...rfi, origin, isDefaultRfiType, rfiTypesFieldsInUse });
    const { eventName, properties } = trackingEventObj;
    trackEvent(eventName, properties);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Store invalid custom identifiers from BE.
  const [invalidIdentifiers, setInvalidIdentifiers] = useState<any>([]);

  const [selectedTransition, setSelectedTransition] = useState<StatusOption>();
  const [prevSelectedTransition, setPrevSelectedTransition] = useState<StatusOption>();

  // Set initial transition (e.g - draft, open, openRev1)
  const defaultRfiTypeWfType = rfiTypes?.find(({ isDefault }) => isDefault)?.wfType as WorkflowType;
  const statusOptions = useStatusOptions(defaultRfiTypeWfType);
  useEffect(() => {
    if (statusOptions) {
      const selectedTransition = getSelectedTransition(statusOptions);
      setSelectedTransition(selectedTransition);
    }
  }, [statusOptions]);

  // Show no workflow configuration when selection has no assignees
  const showNoWorkflowConfiguration = isEmpty(selectedTransition?.assigneeUsers);

  // handling customIdentifier visibility state according to selected status
  const isRfiCustomIdentifierVisible = selectedTransition?.serverStatus !== RfiStatus.Draft;

  // Define validation schema according to selected status
  const mandatoryFields = getMandatoryFields(selectedTransition);
  const validationSchema = useMemo(
    () =>
      yup.object(
        mandatoryFields.reduce((schema: any, field: any) => {
          switch (field) {
            case 'title':
              return {
                ...schema,
                [field]: yup
                  .string()
                  .min(1, translate('new.fieldRequired')) // The default is empty string so we need at least 1 character for it to be valid
                  .required(translate('new.fieldRequired')),
              };
            case 'assignedTo':
              return {
                ...schema,
                [field]: yup
                  .array()
                  .of(
                    yup.object({
                      id: yup.string().required(),
                      type: yup.string(),
                    }),
                  )
                  .min(1, translate('new.fieldRequired'))
                  .required(translate('new.fieldRequired')),
              };
            case 'customIdentifier':
              return {
                ...schema,
                [field]: yup
                  .string()
                  .test(
                    'allowed-characters',
                    translate('new.rfiCustomIdentifierAllowedCharactersValidationMessage'),
                    rfiCustomIdentifierAllowedCharactersValidator,
                  )
                  .max(15, translate('new.rfiCustomIdentifierMaxCharactersValidationMessage'))
                  .required(translate('new.rfiCustomIdentifierFieldRequired'))
                  .test(
                    'valid-be-values',
                    translate('new.error.customIdentifierNotAvailable'),
                    (value) => !invalidIdentifiers.includes(value),
                  ),
              };
            default:
              return schema;
          }
        }, {}),
      ),
    [invalidIdentifiers, mandatoryFields],
  );

  // When adding new fields to the form make sure to add them here as well.
  const defaultValues = {
    [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: [],
    [ISSUE_ATTRIBUTE_NAMES.CUSTOM_IDENTIFIER]: '',
    [ISSUE_ATTRIBUTE_NAMES.QUESTION]: '',
    [ISSUE_ATTRIBUTE_NAMES.DUE_DATE]: '',
    [ISSUE_ATTRIBUTE_NAMES.LOCATION_DESCRIPTION]: '',
    [ISSUE_ATTRIBUTE_NAMES.PRIORITY]: RfiPriority.Normal,
    [ISSUE_ATTRIBUTE_NAMES.STATUS]: selectedTransition?.serverStatus,
    [ISSUE_ATTRIBUTE_NAMES.SUGGESTED_ANSWER]: '',
    [ISSUE_ATTRIBUTE_NAMES.TITLE]: '',
    [ISSUE_ATTRIBUTE_NAMES.RFI_TYPE_ID]: '',
    [ISSUE_ATTRIBUTE_NAMES.WATCHERS]: [],
  };

  if (usersMe.isFetching || !selectedTransition) {
    return { ready: false, defaultValues };
  }

  // define refs for fields, used to scroll into view on error
  const refs: any = {};
  Object.keys(defaultValues).forEach((field) => {
    refs[field] = createRef();
  });

  const createRfi = async (values: any, formik: any, dueDateTimeZone: string | undefined) => {
    setIsSubmitting(true);
    try {
      const sdk = createSDK({ projectId } as any);
      // TODO - SCPJM-8072 - combine these two functions to one
      const formattedFormValues = formatFormValuesForSubmission(values, {
        isRfiCustomIdentifierVisible,
      });
      const serializedRfi = serializeRfi(formattedFormValues, { dueDateTimeZone });
      const newRfi = await sdk.RFIs.create(serializedRfi);
      if (featureFlag.notifications) {
        showSuccessNotification(newRfi);
      }
      if (featureFlag.analytics) {
        trackCreateRfi(newRfi);
      }
      setIsSubmitting(false);
      formik.resetForm();
      return newRfi;
    } catch (err) {
      if (featureFlag.notifications) {
        showErrorNotification();
      }
      setIsSubmitting(false);
      return false;
    }
  };

  return {
    setInvalidIdentifiers,
    setPrevSelectedTransition,
    setSelectedTransition,
    createRfi,
    defaultValues,
    invalidIdentifiers,
    isRfiCustomIdentifierVisible,
    isSubmitting,
    mandatoryFields,
    prevSelectedTransition,
    projectId,
    ready: true,
    refs,
    selectedTransition,
    showNoWorkflowConfiguration,
    validationSchema,
  };
};
const RfiFormProvider = ({ children, origin, featureFlag }: any): ReactNode => {
  const ctx = useRfiFormLogic({ origin, featureFlag });
  return <RfiFormContext.Provider value={ctx as any}>{children}</RfiFormContext.Provider>;
};
RfiFormProvider.propTypes = {
  /**
   * The children to render inside
   */
  children: PropTypes.node.isRequired,
  /**
   * where this rfi originated from, this is for analytics, e.g 'Issues`
   */
  origin: PropTypes.string,
  featureFlag: PropTypes.shape({
    analytics: PropTypes.bool,
    notifications: PropTypes.bool,
  }),
};

RfiFormProvider.defaultProps = {
  origin: 'NOT SET',
  featureFlag: {
    analytics: true,
    notifications: true,
  },
};

export { RfiFormContext, useRfiForm, RfiFormProvider };
export default RfiFormProvider;
